import { Link } from '@mui/material';
import { MuiRouterLink, MuiRouterLinkProps } from 'components/Link/MuiRouterLink';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  checkOmniNavOrigin,
  useAnalytics,
} from 'providers/contexts/analytics/AnalyticsContext';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDataProvider, useResourceContext } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useSearch } from '../SearchContextProvider';
import { GenericSearchItem } from './SearchItemsProvider';

export type SearchLinkWrapperProps = MuiRouterLinkProps & {
  record?: GenericSearchItem;
  children?: React.ReactNode;
};

const SearchLinkWrapper = memo(
  ({
    record,
    children,
    to,
    target,
    onClick: propOnClick,
    ...rest
  }: SearchLinkWrapperProps) => {
    const { trackEvent, setTrackNav, trackNav } = useAnalytics();
    const { getSearchItemInfo } = useSearch();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const resource = useResourceContext();
    const location = useLocation();
    const navigate = useNavigate();
    const { activeSite } = useCosmos();
    const [isProcessing, setIsProcessing] = useState(false);

    const isWireSearch = useMemo(() => {
      return resource === 'wire/channel/search';
    }, [resource]);

    // Currently seems like wire/channel/search 'syndication' (feeds) needs
    // to perform some prechecks before build and nav to the URL.
    // unforutnatley no href support there for ctrl click, new tab, etc...
    // but for most other views we can rely on the passed in url via props
    // or the getSearchItemInfo function to generate the url and maintain
    // href support so users can middle click or ctrl+click to
    // open in new tab at the browser level, etc.
    const propsUrl = isWireSearch ? undefined : to;
    const propsTarget = isWireSearch ? undefined : target;

    const handleWireSearchClick = useCallback(
      async (event: React.MouseEvent) => {
        // prevent defaults
        event.preventDefault();
        event.stopPropagation();

        // ensure correct handler
        if (!isWireSearch) {
          console.warn('[handleWireSearchClick] Incorrect handler.');
          return;
        }

        // prevent multiple clicks while processing
        if (!record || isProcessing) {
          console.warn('[handleWireSearchClick] Missing data or processing.');
          return;
        }

        // build nav route from the record and import status
        try {
          setIsProcessing(true);
          // Special case for wire/channel/search resource
          // If the record is imported, we need to fetch the fresh record
          // to get the asset_uuid and construct the search item URL.
          if (record?.status === 'imported') {
            const channel = new URLSearchParams(location.search)?.get('channel_id');
            if (!channel) {
              console.error('[handleWireSearchClick] Missing channel id.');
              setIsProcessing(false);
              return;
            }
            const response = await dataProvider.getWireImportStatus(channel, [
              record?.object_id,
            ]);
            const freshRecord = response?.data?.[0];
            if (freshRecord && freshRecord.asset_uuid) {
              // imported so we can go to the editing view
              const freshRecordUrl = `/${activeSite}/editorial/edit/${freshRecord?.asset_uuid}/`;
              window.open(freshRecordUrl, '_blank');
            } else {
              console.warn('[handleWireSearchClick] Blocked, missing import asset_uuid');
              setIsProcessing(false);
              return;
            }
          } else {
            // Still in wire/channel/search, but not imported yet
            // use helper getSearchItemInfo function to get the URL and navigate
            const itemInfo = getSearchItemInfo(record);
            if (itemInfo && itemInfo?.url) {
              navigate(itemInfo.url);
            } else {
              console.warn('[handleWireSearchClick] Blocked, missing search item URL');
            }
          }
        } catch (error) {
          console.error('[handleWireSearchClick] Error:', error);
        } finally {
          setIsProcessing(false);
        }
      },
      [
        isWireSearch,
        record,
        isProcessing,
        location.search,
        dataProvider,
        activeSite,
        navigate,
        getSearchItemInfo,
      ],
    );

    const handleClick = useCallback(
      async (event: React.MouseEvent) => {
        // If there's a prop onClick (e.g., from modal search), call it first
        if (propOnClick) {
          propOnClick(event as any);
          return; // Let the prop handler control behavior
        }

        // Track analytics if coming from OmniSearch
        const { isFromOmniSearch, omniQuery } = checkOmniNavOrigin(trackNav);

        if (isFromOmniSearch) {
          // send analytics event for omni search click
          trackEvent({
            event: 'Admin Omni Search Result Selected',
            enabled: isFromOmniSearch,
            data: {
              query: omniQuery,
              result_title: record?.type === 'user' ? record?.email : record?.title,
              result_asset_id: record?.uuid,
            },
          });
          // clear nav tracking event
          setTrackNav(null);
        }

        // handoff to wire search click handler
        if (isWireSearch) {
          await handleWireSearchClick(event);
          return;
        }

        // prevent on items without url/href
        if (!propsUrl) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      },
      [
        propOnClick,
        trackEvent,
        record?.type,
        record?.email,
        record?.title,
        record?.uuid,
        isWireSearch,
        propsUrl,
        handleWireSearchClick,
        setTrackNav,
        trackNav,
      ],
    );

    // guard as wire has more advanced logic
    // it needs to resolve the href before navigating
    if (isWireSearch) {
      return (
        <Link
          underline="none"
          color="inherit"
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            display: 'inline-block',
          }}
          {...rest}
        >
          {children}
        </Link>
      );
    }

    return (
      <MuiRouterLink
        to={propsUrl} // this is the href attr
        target={propsTarget}
        underline="none"
        color="inherit"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          display: 'inline-block',
        }}
        {...rest}
      >
        {children}
      </MuiRouterLink>
    );
  },
);

export default SearchLinkWrapper;
